import { DASHBOARD_PERMISSIONS, PERMISSIONS as P, type PermissionKey } from '@igoal/shared';
import { QueryClientProvider } from '@tanstack/react-query';
import { lazy, StrictMode, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router';
import { firstAllowedPath, Layout } from './components/Layout';
import { Spinner, ToastProvider } from './components/ui';
import './index.css';
import { queryClient } from './lib/api';
import { AuthProvider, useAuth } from './lib/auth';
import { ThemeProvider } from './lib/theme';
import { ChangePasswordPage, LoginPage } from './pages/auth';

// Each screen is its own chunk, loaded when first opened; the shell, sign-in and UI kit stay in the main bundle
const AuditPage = lazy(() => import('./pages/AdminPages').then((x) => ({ default: x.AuditPage })));
const DevicesPage = lazy(() => import('./pages/AdminPages').then((x) => ({ default: x.DevicesPage })));
const SettingsPage = lazy(() => import('./pages/AdminPages').then((x) => ({ default: x.SettingsPage })));
const TimezonePage = lazy(() => import('./pages/AdminPages').then((x) => ({ default: x.TimezonePage })));
const CategoriesPage = lazy(() => import('./pages/CategoriesPage').then((x) => ({ default: x.CategoriesPage })));
const ChinaOrderPage = lazy(() => import('./pages/ChinaOrderPage').then((x) => ({ default: x.ChinaOrderPage })));
const ChinaOrdersPage = lazy(() => import('./pages/ChinaOrdersPage').then((x) => ({ default: x.ChinaOrdersPage })));
const OrderTrackerPage = lazy(() => import('./pages/OrderTrackerPage').then((x) => ({ default: x.OrderTrackerPage })));
const ShipmentPage = lazy(() => import('./pages/ShipmentPage').then((x) => ({ default: x.ShipmentPage })));
const ShipmentsPage = lazy(() => import('./pages/ShipmentsPage').then((x) => ({ default: x.ShipmentsPage })));
const InvoicesPage = lazy(() => import('./pages/InvoicesPage').then((x) => ({ default: x.InvoicesPage })));
const TransfersPage = lazy(() => import('./pages/TransfersPage').then((x) => ({ default: x.TransfersPage })));
const ShopOrdersPage = lazy(() => import('./pages/ShopOrdersPage').then((x) => ({ default: x.ShopOrdersPage })));
const ShopOrderPage = lazy(() => import('./pages/ShopOrderPage').then((x) => ({ default: x.ShopOrderPage })));
const ShopOrderFormPage = lazy(() => import('./pages/ShopOrderFormPage').then((x) => ({ default: x.ShopOrderFormPage })));
const NotificationSettingsPage = lazy(() => import('./pages/NotificationSettingsPage').then((x) => ({ default: x.NotificationSettingsPage })));
const AdjustmentsPage = lazy(() => import('./pages/AdjustmentsPage').then((x) => ({ default: x.AdjustmentsPage })));
const AdjustmentPage = lazy(() => import('./pages/AdjustmentPage').then((x) => ({ default: x.AdjustmentPage })));
const TransferPage = lazy(() => import('./pages/TransferPage').then((x) => ({ default: x.TransferPage })));
const DashboardPage = lazy(() => import('./pages/DashboardPage').then((x) => ({ default: x.DashboardPage })));
const BranchesPage = lazy(() => import('./pages/BranchesPage').then((x) => ({ default: x.BranchesPage })));
const ItemsPage = lazy(() => import('./pages/ItemsPage').then((x) => ({ default: x.ItemsPage })));
const NewChinaOrderPage = lazy(() => import('./pages/NewChinaOrderPage').then((x) => ({ default: x.NewChinaOrderPage })));
const ReportPage = lazy(() => import('./pages/ReportsPage').then((x) => ({ default: x.ReportPage })));
const RolesPage = lazy(() => import('./pages/RolesPage').then((x) => ({ default: x.RolesPage })));
const StockPage = lazy(() => import('./pages/StockPage').then((x) => ({ default: x.StockPage })));
const SuppliersPage = lazy(() => import('./pages/SuppliersPage').then((x) => ({ default: x.SuppliersPage })));
const UsersPage = lazy(() => import('./pages/UsersPage').then((x) => ({ default: x.UsersPage })));

const SHOP_ORDER_VIEWERS: PermissionKey[] = [P.STOCK_REQUESTS_CREATE, P.STOCK_REQUESTS_APPROVE, P.TRANSFERS_DISPATCH, P.BRANCH_ORDERS_SUPPLIER_VIEW];
const SHIPMENT_VIEWERS: PermissionKey[] = [P.SHIPMENTS_VIEW_ALL, P.SHIPMENTS_VIEW_LOCATION, P.SHIPMENTS_MANAGE, P.CHINA_ORDERS_RECEIVE];

function RequireAuth({ children }: { children: ReactNode }) {
  const { me, loading } = useAuth();
  const location = useLocation();
  if (loading) return <Spinner />;
  if (!me) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  if (me.mustChangePassword && location.pathname !== '/account/password') return <Navigate to="/account/password" replace />;
  return <>{children}</>;
}

function Allow({ perm, children }: { perm: PermissionKey; children: ReactNode }) {
  const { can } = useAuth();
  if (!can(perm)) return <NoAccess />;
  return <>{children}</>;
}

function AllowAny({ perms, children }: { perms: PermissionKey[]; children: ReactNode }) {
  const { can } = useAuth();
  if (!perms.some((p) => can(p))) return <NoAccess />;
  return <>{children}</>;
}

/** The dashboard for users with something to show there, otherwise their first screen */
function Home() {
  const { can, me } = useAuth();
  if (DASHBOARD_PERMISSIONS.some((p) => can(p))) return <DashboardPage />;
  const to = firstAllowedPath(can);
  if (to) return <Navigate to={to} replace />;
  return (
    <div className="max-w-prose py-10">
      <h1 className="mb-2 text-xl font-semibold">Welcome, {me?.fullName}</h1>
      <p className="text-ink-soft">Your role ({me?.role.name}) has no screens yet. Contact your administrator if you expected to see something here.</p>
    </div>
  );
}

function NoAccess() {
  return (
    <div className="max-w-prose py-10">
      <h1 className="mb-2 text-xl font-semibold">No access</h1>
      <p className="text-ink-soft">Your role doesn't include this screen. Ask an administrator if you need it.</p>
    </div>
  );
}

/** Orders used to live at /china-orders; keep old bookmarks and shared links working */
function OldOrdersPath() {
  const { pathname, search } = useLocation();
  return <Navigate to={pathname.replace(/^\/china-orders/, '/orders') + search} replace />;
}

function PasswordRoute() {
  const { me } = useAuth();
  if (me?.mustChangePassword) return <ChangePasswordPage />;
  return <Layout />;
}

function App() {
  const { me, loading } = useAuth();
  if (loading) return <Spinner />;
  return (
    <Routes>
      <Route path="/login" element={me ? <Navigate to="/" replace /> : <LoginPage />} />
      <Route
        path="/account/password"
        element={
          <RequireAuth>
            <PasswordRoute />
          </RequireAuth>
        }
      >
        <Route index element={<ChangePasswordPage />} />
      </Route>
      <Route
        element={
          <RequireAuth>
            <Layout />
          </RequireAuth>
        }
      >
        <Route index element={<Home />} />
        <Route path="stock" element={<Allow perm={P.STOCK_VIEW}><StockPage /></Allow>} />
        <Route path="china-orders/*" element={<OldOrdersPath />} />
        <Route path="orders" element={<AllowAny perms={[P.CHINA_ORDERS_VIEW, P.CHINA_PORTAL_ACCESS, P.CHINA_ORDERS_RECEIVE]}><ChinaOrdersPage /></AllowAny>} />
        <Route path="orders/new" element={<Allow perm={P.CHINA_ORDERS_MANAGE}><NewChinaOrderPage /></Allow>} />
        <Route path="orders/:id" element={<AllowAny perms={[P.CHINA_ORDERS_VIEW, P.CHINA_PORTAL_ACCESS, P.CHINA_ORDERS_RECEIVE]}><ChinaOrderPage /></AllowAny>} />
        <Route path="reports" element={<Navigate to="/reports/stock-by-location" replace />} />
        <Route path="reports/:report" element={<Allow perm={P.REPORTS_VIEW}><ReportPage /></Allow>} />
        <Route path="order-tracker" element={<AllowAny perms={[P.CHINA_ORDERS_VIEW, P.CHINA_PORTAL_ACCESS, P.CHINA_ORDERS_RECEIVE]}><OrderTrackerPage /></AllowAny>} />
        <Route path="shipments" element={<AllowAny perms={SHIPMENT_VIEWERS}><ShipmentsPage /></AllowAny>} />
        <Route path="shipments/:id" element={<AllowAny perms={SHIPMENT_VIEWERS}><ShipmentPage /></AllowAny>} />
        <Route path="invoices" element={<AllowAny perms={[P.SHIPMENTS_VIEW_ALL, P.SHIPMENTS_MANAGE, P.TRANSFERS_DISPATCH]}><InvoicesPage /></AllowAny>} />
        <Route path="transfers" element={<AllowAny perms={[P.TRANSFERS_DISPATCH, P.TRANSFERS_ACKNOWLEDGE]}><TransfersPage /></AllowAny>} />
        <Route path="transfers/:id" element={<AllowAny perms={[P.TRANSFERS_DISPATCH, P.TRANSFERS_ACKNOWLEDGE]}><TransferPage /></AllowAny>} />
        <Route path="shop-orders" element={<AllowAny perms={SHOP_ORDER_VIEWERS}><ShopOrdersPage /></AllowAny>} />
        <Route path="shop-orders/new" element={<Allow perm={P.STOCK_REQUESTS_CREATE}><ShopOrderFormPage /></Allow>} />
        <Route path="shop-orders/:id" element={<AllowAny perms={SHOP_ORDER_VIEWERS}><ShopOrderPage /></AllowAny>} />
        <Route path="shop-orders/:id/edit" element={<Allow perm={P.STOCK_REQUESTS_CREATE}><ShopOrderFormPage /></Allow>} />
        <Route path="adjustments" element={<AllowAny perms={[P.STOCK_ADJUST, P.STOCK_ADJUST_APPROVE]}><AdjustmentsPage /></AllowAny>} />
        <Route path="adjustments/:id" element={<AllowAny perms={[P.STOCK_ADJUST, P.STOCK_ADJUST_APPROVE]}><AdjustmentPage /></AllowAny>} />
        <Route path="items" element={<Allow perm={P.ITEMS_VIEW}><ItemsPage /></Allow>} />
        <Route path="suppliers" element={<Allow perm={P.SUPPLIERS_VIEW}><SuppliersPage /></Allow>} />
        <Route path="categories" element={<Allow perm={P.ITEMS_VIEW}><CategoriesPage /></Allow>} />
        <Route path="branches" element={<Allow perm={P.BRANCHES_VIEW}><BranchesPage /></Allow>} />
        <Route path="users" element={<Allow perm={P.USERS_VIEW}><UsersPage /></Allow>} />
        <Route path="roles" element={<Allow perm={P.ROLES_VIEW}><RolesPage /></Allow>} />
        <Route path="audit" element={<Allow perm={P.AUDIT_VIEW}><AuditPage /></Allow>} />
        <Route path="settings" element={<Allow perm={P.SETTINGS_MANAGE}><SettingsPage /></Allow>} />
        <Route path="account/devices" element={<DevicesPage />} />
        <Route path="account/timezone" element={<TimezonePage />} />
        <Route path="account/notifications" element={<NotificationSettingsPage />} />
        <Route path="*" element={<NoAccess />} />
      </Route>
    </Routes>
  );
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthProvider>
            <ToastProvider>
              <App />
            </ToastProvider>
          </AuthProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </ThemeProvider>
  </StrictMode>,
);
